/**
 * Báo cáo – Thống kê thụ lý, năng suất (Chánh án). Dữ liệu từ danh sách vụ án.
 */
import { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Assessment, Folder, Schedule, TrendingUp, Download, NavigateNext } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useGetCasesQuery } from '../../store/api/casesApi'
import type { CaseStatus } from '../../types/case'

const statusLabels: Record<CaseStatus, string> = {
  draft: 'Nháp',
  filed: 'Đã nộp',
  accepted: 'Đã thụ lý',
  pre_trial: 'Chuẩn bị xét xử',
  trial: 'Đang xét xử',
  closed: 'Đã kết thúc',
  returned: 'Trả đơn',
  transferred: 'Chuyển đơn',
}

const CATEGORY_LABELS: Record<string, string> = {
  marriage_family: 'Hôn nhân và gia đình',
  civil: 'Dân sự',
  land: 'Đất đai',
  business: 'Kinh doanh thương mại',
}

const STATUS_ORDER: CaseStatus[] = ['filed', 'accepted', 'pre_trial', 'trial', 'closed', 'returned', 'transferred', 'draft']

const PERIODS = [
  { value: '2026-03', label: 'Tháng 3/2026' },
  { value: '2026-Q1', label: 'Quý 1/2026' },
  { value: '2026', label: 'Năm 2026' },
]

export default function ReportsPage() {
  const navigate = useNavigate()
  const { data: cases = [] } = useGetCasesQuery()
  const [period, setPeriod] = useState('2026-03')

  const total = cases.length
  const byStatus = cases.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] ?? 0) + 1
    return acc
  }, {})
  const closed = byStatus.closed ?? 0
  const inProgress = total - closed
  const resolveRate = total ? Math.round((closed / total) * 100) : 0

  const byCategory = cases.reduce<Record<string, number>>((acc, c) => {
    const cat = c.category || 'other'
    acc[cat] = (acc[cat] ?? 0) + 1
    return acc
  }, {})

  const byJudge = cases.reduce<Record<string, { total: number; closed: number }>>((acc, c) => {
    const name = c.assigned_judge_name || 'Chưa phân công'
    if (!acc[name]) acc[name] = { total: 0, closed: 0 }
    acc[name].total += 1
    if (c.status === 'closed') acc[name].closed += 1
    return acc
  }, {})
  const judgeRows = Object.entries(byJudge).sort((a, b) => b[1].total - a[1].total)

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
        <Link component="button" variant="body2" underline="hover" color="inherit" onClick={() => navigate('/workspace')}>Bảng điều khiển</Link>
        <Typography variant="body2" color="text.primary">Báo cáo</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Báo cáo thống kê vụ án
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tổng hợp thụ lý, tiến độ xử lý và tỷ lệ giải quyết. Phục vụ chỉ đạo và báo cáo cấp trên.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Kỳ báo cáo</InputLabel>
            <Select value={period} label="Kỳ báo cáo" onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" startIcon={<Download />}>Xuất báo cáo</Button>
        </Box>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Card sx={{ height: '100%' }} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Folder color="primary" />
                <Typography variant="subtitle2" color="text.secondary">Tổng vụ án (đã thụ lý/đang xử lý)</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>{total}</Typography>
              <Typography variant="caption" color="text.secondary">vụ</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Card sx={{ height: '100%' }} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Schedule color="primary" />
                <Typography variant="subtitle2" color="text.secondary">Đang xử lý</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>{inProgress}</Typography>
              <Typography variant="caption" color="text.secondary">vụ</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Card sx={{ height: '100%' }} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Assessment color="success" />
                <Typography variant="subtitle2" color="text.secondary">Đã kết thúc</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>{closed}</Typography>
              <Typography variant="caption" color="text.secondary">vụ</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Card sx={{ height: '100%' }} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp color="primary" />
                <Typography variant="subtitle2" color="text.secondary">Tỷ lệ giải quyết</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>{resolveRate}%</Typography>
              <Typography variant="caption" color="text.secondary">trên tổng thụ lý</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card sx={{ mt: 3, width: '100%' }} variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Phân bố theo trạng thái tố tụng
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Số vụ theo từng giai đoạn (đã nộp, đã thụ lý, chuẩn bị xét xử, đang xét xử, đã kết thúc, trả/chuyển đơn).
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {STATUS_ORDER.filter((s) => (byStatus[s] ?? 0) > 0).map((status) => {
              const count = byStatus[status] ?? 0
              const pct = total ? (count / total) * 100 : 0
              return (
                <Box key={status}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>{statusLabels[status]}</Typography>
                    <Typography variant="body2" color="text.secondary">{count} vụ · {pct.toFixed(0)}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} color={status === 'closed' ? 'success' : 'primary'} />
                </Box>
              )
            })}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, width: '100%' }} variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Phân bố theo loại vụ việc
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Hôn nhân & gia đình, dân sự, đất đai, kinh doanh thương mại.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(byCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, count]) => {
                const pct = total ? (count / total) * 100 : 0
                const label = CATEGORY_LABELS[cat] || cat
                return (
                  <Box key={cat}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary">{count} vụ · {pct.toFixed(0)}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} />
                  </Box>
                )
              })}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, width: '100%' }} variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Theo Thẩm phán được phân công
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Số vụ đang xử lý và đã kết thúc theo từng Thẩm phán.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Thẩm phán</strong></TableCell>
                  <TableCell align="right"><strong>Đang xử lý</strong></TableCell>
                  <TableCell align="right"><strong>Đã kết thúc</strong></TableCell>
                  <TableCell align="right"><strong>Tổng</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {judgeRows.map(([name, stat]) => (
                  <TableRow key={name}>
                    <TableCell sx={{ fontWeight: 500 }}>{name}</TableCell>
                    <TableCell align="right">{stat.total - stat.closed}</TableCell>
                    <TableCell align="right">{stat.closed}</TableCell>
                    <TableCell align="right">{stat.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  )
}
